import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { SupplierRoutePath } from 'modules/FreeSupplier/routes';
import { useMockData } from '../state/MockDataContext';
import UpsellCard from '../components/UpsellCard';
import ReadinessScore from '../components/gaps/ReadinessScore';
import GapList from '../components/gaps/GapList';
import { SupplierChecklistSkeleton } from '../components/SupplierLoadingStates';

// B5: navigation data uses the canonical SupplierRoutePath constants, never
// hardcoded path literals — the single source of truth shared with the nav
// (supplierNavigation.js) and every route builder.
//
// Fixpack #15 (D-15): the checklist walks the FULL draft/publish cycle, not
// just "add data once" — add as drafts, publish, edit, withdraw, discard —
// so the getting-started page teaches the review workflow the whole product
// is built around, not only the happy path. Completion truth for every key
// lives in deriveSupplierChecklist (state/checklist.js).
//
// Builder (not a static array) so every label/hint/cta routes through i18n —
// same pattern as buildCustomsProcedures (Imports/data/shipmentConstants.js).
const buildSteps = (t) => [
  {
    key: 'companySaved',
    title: t(
      'freeSupplier:gettingStarted.steps.companySaved.title',
      'Complete your company profile',
    ),
    hint: t(
      'freeSupplier:gettingStarted.steps.companySaved.hint',
      'Your operator details appear on everything you share.',
    ),
    path: SupplierRoutePath.Company,
    cta: t('freeSupplier:gettingStarted.steps.companySaved.cta', 'Complete profile'),
  },
  {
    key: 'hasInstallation',
    title: t('freeSupplier:gettingStarted.steps.hasInstallation.title', 'Add your installation'),
    hint: t(
      'freeSupplier:gettingStarted.steps.hasInstallation.hint',
      'The production site your CBAM goods come from.',
    ),
    path: SupplierRoutePath.Installations,
    cta: t('freeSupplier:gettingStarted.steps.hasInstallation.cta', 'Add installation'),
  },
  {
    key: 'addedDraftValues',
    title: t(
      'freeSupplier:gettingStarted.steps.addedDraftValues.title',
      'Add emissions data as drafts',
    ),
    hint: t(
      'freeSupplier:gettingStarted.steps.addedDraftValues.hint',
      'Upload a CBAM template or add values manually — they land as drafts you can review.',
    ),
    path: SupplierRoutePath.Drafts,
    cta: t('freeSupplier:gettingStarted.steps.addedDraftValues.cta', 'Add draft data'),
  },
  {
    key: 'publishedFactor',
    title: t(
      'freeSupplier:gettingStarted.steps.publishedFactor.title',
      'Publish an emissions value',
    ),
    hint: t(
      'freeSupplier:gettingStarted.steps.publishedFactor.hint',
      'Review a draft, then Confirm & publish to share it with your customers.',
    ),
    path: SupplierRoutePath.Drafts,
    cta: t('freeSupplier:gettingStarted.steps.publishedFactor.cta', 'Review drafts'),
  },
  {
    key: 'editedFactor',
    title: t('freeSupplier:gettingStarted.steps.editedFactor.title', 'Edit a published value'),
    hint: t(
      'freeSupplier:gettingStarted.steps.editedFactor.hint',
      'Correct a figure and we version it automatically — nothing is lost.',
    ),
    path: SupplierRoutePath.EmissionFactors,
    cta: t('freeSupplier:gettingStarted.steps.editedFactor.cta', 'Edit a value'),
  },
  {
    key: 'withdrawnFactor',
    title: t('freeSupplier:gettingStarted.steps.withdrawnFactor.title', 'Withdraw a value'),
    hint: t(
      'freeSupplier:gettingStarted.steps.withdrawnFactor.hint',
      'Pull a published figure back if it no longer applies.',
    ),
    path: SupplierRoutePath.EmissionFactors,
    cta: t('freeSupplier:gettingStarted.steps.withdrawnFactor.cta', 'Withdraw a value'),
  },
  {
    key: 'discardedDraft',
    title: t('freeSupplier:gettingStarted.steps.discardedDraft.title', 'Discard a draft'),
    hint: t(
      'freeSupplier:gettingStarted.steps.discardedDraft.hint',
      'Clear out a draft you decide not to publish.',
    ),
    path: SupplierRoutePath.Drafts,
    cta: t('freeSupplier:gettingStarted.steps.discardedDraft.cta', 'Review drafts'),
  },
  {
    key: 'portalShared',
    title: t('freeSupplier:gettingStarted.steps.portalShared.title', 'Enable your public portal'),
    hint: t(
      'freeSupplier:gettingStarted.steps.portalShared.hint',
      'One public link answers every customer data request.',
    ),
    path: SupplierRoutePath.Share,
    cta: t('freeSupplier:gettingStarted.steps.portalShared.cta', 'Publish portal'),
  },
  {
    key: 'requestHandled',
    title: t('freeSupplier:gettingStarted.steps.requestHandled.title', 'Respond to a data request'),
    hint: t(
      'freeSupplier:gettingStarted.steps.requestHandled.hint',
      'Review who asked, the CN codes they need, and the reporting period.',
    ),
    path: SupplierRoutePath.Inbox,
    cta: t('freeSupplier:gettingStarted.steps.requestHandled.cta', 'View requests'),
  },
];

export default function GettingStartedPage() {
  const { t } = useTranslation('freeSupplier');
  const STEPS = useMemo(() => buildSteps(t), [t]);
  const { checklist, completedSteps, totalSteps, gaps, readinessScore, checklistLoading } =
    useMockData();

  // Every checklist key derives to `false` while its query is pending, so
  // rendering early would claim "0 of 9 steps completed" with nothing ticked —
  // a supplier who has finished six steps would watch their progress get wiped
  // and restored on each visit. Hold the real silhouette until the truth lands.
  if (checklistLoading) return <SupplierChecklistSkeleton steps={STEPS.length} />;

  return (
    <div className="flex flex-col gap-6">
      {/* Hero */}
      <div className="rounded-2xl bg-white border border-slate-200 p-6">
        <p className="text-[10px] font-bold uppercase tracking-wider text-primaryColor m-0">
          {t('freeSupplier:gettingStarted.eyebrow', 'Free plan · Supplier journey')}
        </p>
        <h1 className="text-2xl font-bold text-slate-800 mt-1 mb-1">
          {t(
            'freeSupplier:gettingStarted.heroTitle',
            'Share your emissions data with EU customers in minutes',
          )}
        </h1>
        <p className="text-sm text-slate-500 m-0 max-w-xl">
          {t(
            'freeSupplier:gettingStarted.heroDescription',
            'Centralize your operator data, installations and emission factors once — then share them through a public portal link, data requests, or an embeddable widget. No more spreadsheet email chains.',
          )}
        </p>

        {/* Progress */}
        <div className="mt-5">
          {/* Q5 (S7 fix batch 3): ReadinessScore's label is `whitespace-nowrap`
              and its bar a fixed `w-24` — neither shrinks, so at 390px the two
              children's combined minimum width overflowed the card. `flex-wrap`
              lets the score drop to its own line instead of spilling out. */}
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1.5 mb-1.5">
            <span className="text-xs font-semibold text-slate-600">
              {t(
                'freeSupplier:gettingStarted.stepsCompleted',
                '{{completed}} of {{total}} steps completed',
                {
                  completed: completedSteps,
                  total: totalSteps,
                },
              )}
            </span>
            <ReadinessScore score={readinessScore} />
          </div>
          <div className="flex gap-1">
            {STEPS.map((step) => (
              <div
                key={step.key}
                className={`h-1.5 flex-1 rounded-full ${
                  checklist[step.key] ? 'bg-primaryColor' : 'bg-slate-200'
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Checklist */}
      <div className="rounded-2xl bg-white border border-slate-200 divide-y divide-slate-100">
        {STEPS.map((step, idx) => {
          const done = checklist[step.key];
          return (
            <div
              key={step.key}
              className="flex items-center gap-4 px-5 py-4"
            >
              <div
                className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 text-xs font-bold ${
                  done
                    ? 'bg-primaryColor text-white'
                    : 'bg-slate-100 text-slate-400 ring-1 ring-slate-200'
                }`}
              >
                {done ? <FontAwesomeIcon icon={faCheck} /> : idx + 1}
              </div>
              <div className="flex-1 min-w-0">
                <p
                  className={`text-sm font-semibold m-0 ${
                    done ? 'text-slate-400 line-through' : 'text-slate-800'
                  }`}
                >
                  {step.title}
                </p>
                <p className="text-xs text-slate-400 m-0 mt-0.5">{step.hint}</p>
              </div>
              <Link
                to={step.path}
                className={`shrink-0 inline-flex items-center gap-1.5 text-xs font-semibold rounded-xl px-3 py-1.5 no-underline transition-colors ${
                  done
                    ? 'text-slate-500 bg-slate-100 hover:bg-slate-200'
                    : 'text-white bg-primaryColor hover:bg-primaryColor/90'
                }`}
              >
                {done ? t('freeSupplier:gettingStarted.review', 'Review') : step.cta}
                <FontAwesomeIcon
                  icon={faArrowRight}
                  className="text-[10px]"
                />
              </Link>
            </div>
          );
        })}
      </div>

      {/* Regulatory completeness — the checklist tells you what to do next,
          this tells you what's still missing per CBAM requirements. */}
      <GapList
        gaps={gaps}
        title={t(
          'freeSupplier:gettingStarted.gapsTitle',
          'Data gaps holding your portal readiness back',
        )}
        maxItems={3}
      />

      <UpsellCard
        feature="generic"
        title={t('freeSupplier:gettingStarted.upsellTitle', "What Free doesn't include")}
        body={t(
          'freeSupplier:gettingStarted.upsellBody',
          'SEFA emissions calculation, CBAM cost estimates, verification and data insights are Premium — we can calculate everything from the data you enter here.',
        )}
        ctaLabel={t('freeSupplier:gettingStarted.upsellCta', 'Compare plans')}
        dismissKey="home-compare-plans"
      />
    </div>
  );
}
